'use client';

import { createClient } from '@/lib/supabase/client';

export type SubmissionStatus = 'new' | 'contacted' | 'in_progress' | 'resolved' | 'archived';

export interface ContactSubmission {
  id: string;
  name: string;
  email: string;
  phone: string;
  message: string;
  status: SubmissionStatus;
  createdAt: string;
  updatedAt: string;
}

export interface LeadSubmission {
  id: string;
  name: string;
  phone: string;
  email: string;
  course: string;
  status: SubmissionStatus;
  createdAt: string;
  updatedAt: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];

    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

export const submissionService = {
  // Contact form submission
  async createContactSubmission(formData: {
    name: string;
    email: string;
    phone: string;
    message: string;
  }): Promise<ContactSubmission | null> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('contact_submissions')
        .insert({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: formData.message,
          status: 'new',
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Contact submission error:', error.message);
          return null;
        }
      }

      return {
        id: data.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        message: data.message,
        status: data.status,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  // Lead form submission
  async createLeadSubmission(formData: {
    name: string;
    phone: string;
    email: string;
    course: string;
  }): Promise<LeadSubmission | null> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('lead_submissions')
        .insert({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          course: formData.course,
          status: 'new',
        })
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Lead submission error:', error.message);
          return null;
        }
      }

      return {
        id: data.id,
        name: data.name,
        phone: data.phone,
        email: data.email,
        course: data.course,
        status: data.status,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  // Get all contact submissions
  async getAllContactSubmissions(): Promise<ContactSubmission[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('contact_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else if (error.code === 'PGRST116') {
          return [];
        } else {
          console.log('Fetch contact submissions error:', error.message);
          return [];
        }
      }

      return (
        data?.map((row) => ({
          id: row.id,
          name: row.name,
          email: row.email,
          phone: row.phone,
          message: row.message,
          status: row.status,
          createdAt: row.created_at,
          updatedAt: row.updated_at,
        })) || []
      );
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  // Get all lead submissions
  async getAllLeadSubmissions(): Promise<LeadSubmission[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('lead_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else if (error.code === 'PGRST116') {
          return [];
        } else {
          console.log('Fetch lead submissions error:', error.message);
          return [];
        }
      }

      return (
        data?.map((row) => ({
          id: row.id,
          name: row.name,
          phone: row.phone,
          email: row.email,
          course: row.course,
          status: row.status,
          createdAt: row.created_at,
          updatedAt: row.updated_at,
        })) || []
      );
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  // Update submission status
  async updateContactStatus(
    id: string,
    status: SubmissionStatus
  ): Promise<ContactSubmission | null> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('contact_submissions')
        .update({ status })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Update contact status error:', error.message);
          return null;
        }
      }

      return {
        id: data.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        message: data.message,
        status: data.status,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },

  async updateLeadStatus(
    id: string,
    status: SubmissionStatus
  ): Promise<LeadSubmission | null> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('lead_submissions')
        .update({ status })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error.message);
          throw error;
        } else {
          console.log('Update lead status error:', error.message);
          return null;
        }
      }

      return {
        id: data.id,
        name: data.name,
        phone: data.phone,
        email: data.email,
        course: data.course,
        status: data.status,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
      };
    } catch (error: any) {
      console.log('Schema-related error:', error.message);
      throw error;
    }
  },
};